import React, { Fragment, useContext, useMemo } from 'react';
import { PartyRowContainer } from './PartyRowContainer';
import { useSelectCurrentOrder } from '../../../../../selectors/order/selectCurrentOrder';
import { useGetBorrowerBuyerSeller } from './hooks/useGetBorrowerBuyerSeller';
import { useFindButtons } from './hooks/useFindButtons';
import { SummaryButtonProps, useGetSummaryButtonProps } from './hooks/useGetSummaryButtonProps';
import { useGetFilteredBorrowerSellerParties } from './hooks/useGetFilteredBorrowerSellerParties';
import { SummaryContext } from '../../../../shared/Summary/summaryContext';
import { IParty, isBuyerBorrower } from '../../../../../ui-toolkit/orders/interfaces/IParty';
import { Box, Button } from '@zillow/constellation-10';
import { ZimEligibilityTag } from './ZimEligibilityTag';
import { SummaryItem } from 'components/shared/Summary/SummaryItem/SummaryItem';
import { useHasFeatureFlag } from '../../../../../selectors/useHasFeatureFlag';
import { eTransactionCategory } from '../../../../../ui-toolkit/enums/eTransactionType';
import { ePartyType } from '../../../../../ui-toolkit/enums/ePartyType';

export const BorrowerSellerSummary = () => {
  const { filterText } = useContext(SummaryContext);

  const parties = useGetFilteredBorrowerSellerParties();
  const showAddButton = filterText.length === 0 || parties.length > 0;
  const { data: order } = useSelectCurrentOrder();
  const buttonItems = useGetSummaryButtonProps({
    parties,
    transaction_category: order.transaction.transaction_category,
  });

  const { borrowers, buyers, sellers } = useGetBorrowerBuyerSeller(parties);
  const { borrowerButton, buyerButton, sellerButton } = useFindButtons(buttonItems);

  return (
    <>
      <RowAndButton parties={borrowers} partyButton={borrowerButton} label={'Borrower'} showAddButton={showAddButton} />
      <RowAndButton parties={buyers} partyButton={buyerButton} label={'Buyers'} showAddButton={showAddButton} />
      <RowAndButton parties={sellers} partyButton={sellerButton} label={'Seller'} showAddButton={showAddButton} />
    </>
  );
};

const RowAndButton: React.FC<{
  parties: IParty[];
  partyButton?: SummaryButtonProps;
  showAddButton?: boolean;
  label: string;
}> = ({ label, parties, partyButton, showAddButton }) => {
  const { data: orderData } = useSelectCurrentOrder();
  const isMessagingEnabled = useHasFeatureFlag('messaging.integration');

  const zimEligiblePreconditions: boolean = useMemo(() => {
    return (
      // feature flag is active
      isMessagingEnabled &&
      // it's a purchase transaction
      orderData.transaction.transaction_category === eTransactionCategory.Purchase &&
      // Bicca is enabled
      !!orderData.isBiccaEnabled
    );
  }, [isMessagingEnabled, orderData]);

  if (!((parties.length > 0 || partyButton) && showAddButton)) {
    return null;
  }

  return (
    <SummaryItem label={label} tab={'parties'}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '300' }}>
        {parties.map((party, index) => {
          // zim Status is displayed only for persons who are buyers/borrowers
          const hasZimStatus = party.type === ePartyType.person && isBuyerBorrower(party) && zimEligiblePreconditions;
          return (
            <Fragment key={`${party.id}`}>
              <PartyRowContainer party={party} isNew={false} data-test-id={`party-${index}`} />
              {hasZimStatus && <ZimEligibilityTag status={party.zuid ? 'eligible' : 'not-eligible'} />}
            </Fragment>
          );
        })}
      </Box>
      {partyButton && (
        <Button css={{ marginTop: '16' }} size="sm" tone="neutral" onClick={partyButton.onClick}>
          {partyButton.text}
        </Button>
      )}
    </SummaryItem>
  );
};
